import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

/**
 * Minimal shared service with no external dependencies.
 *
 * Holds auth-readiness signals that Firestore listeners across the app
 * can gate themselves on, preventing the race condition where onSnapshot
 * subscribers fire before the Firebase custom token is available — the
 * root cause of "FirebaseError: Missing or insufficient permissions."
 * errors (predominantly on iOS Safari / Private Browsing).
 */
@Injectable({ providedIn: 'root' })
export class AuthStateService {
  /**
   * Emits `true` (and replays to late subscribers) as soon as the Firebase
   * custom token has been obtained and is ready for use. Gate every
   * user-private Firestore onSnapshot listener on this signal:
   *
   *   authState.customTokenReady$.pipe(take(1), switchMap(() => myListener$))
   *
   * ReplaySubject(1) ensures any subscriber that arrives after the token is
   * already ready gets the signal immediately, with no extra delay.
   */
  public readonly customTokenReady$ = new ReplaySubject<true>(1);

  /**
   * True while a token refresh Cloud Function call is in flight.
   * getUid() waits for this to be false before resolving so callers
   * never receive a transient null uid during a refresh cycle.
   */
  public readonly tokenRefreshInProgress$ = new BehaviorSubject<boolean>(false);
}